import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Globe, BookOpen, Languages, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    [{ align: [] }],
    ['clean'],
  ],
};

const TranslationBadge = ({ blogId, status }) => {
  const [info, setInfo] = useState(null);
  const [polling, setPolling] = useState(false);
  const token = localStorage.getItem('admin_token');

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/blogs/${blogId}/translations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInfo(res.data);
      return res.data;
    } catch { return null; }
  };

  useEffect(() => {
    if (status !== 'published') return;
    fetchStatus();
  }, [blogId, status]);

  // Poll while translations are in progress
  useEffect(() => {
    if (!info?.is_translating) return;
    setPolling(true);
    const interval = setInterval(async () => {
      const updated = await fetchStatus();
      if (updated && !updated.is_translating) {
        setPolling(false);
        clearInterval(interval);
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [info?.is_translating]);

  if (status !== 'published' || !info) return null;

  const pct = info.total > 0 ? Math.round((info.completed / info.total) * 100) : 0;

  return (
    <div data-testid={`translation-status-${blogId}`} className="flex items-center gap-2">
      <div className="flex items-center gap-1.5">
        <Languages className="w-3.5 h-3.5 text-blue-500" />
        <span className="text-xs font-medium text-slate-600">
          {info.completed}/{info.total}
        </span>
      </div>
      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${pct === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {info.is_translating && (
        <RefreshCw className="w-3 h-3 text-blue-500 animate-spin" />
      )}
    </div>
  );
};

const AdminBlogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '', category: '', featured_image: '', content: '',
    meta_title: '', meta_description: '', status: 'draft',
  });

  const getToken = () => localStorage.getItem('admin_token');

  useEffect(() => {
    if (!getToken()) { navigate('/admin'); return; }
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/blogs`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setBlogs(res.data);
    } catch { toast.error('Failed to fetch blogs'); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.category || !formData.content) {
      toast.error('Title, category and content are required');
      return;
    }
    setSaving(true);
    try {
      if (editingBlog) {
        await axios.put(`${API}/admin/blogs/${editingBlog.id}`, formData, { headers: { Authorization: `Bearer ${getToken()}` } });
        toast.success('Blog updated! Translations will generate in the background.');
      } else {
        await axios.post(`${API}/admin/blogs`, formData, { headers: { Authorization: `Bearer ${getToken()}` } });
        toast.success('Blog created! Translations will generate in the background.');
      }
      setShowForm(false);
      setEditingBlog(null);
      resetForm();
      fetchBlogs();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleEdit = async (blog) => {
    try {
      const res = await axios.get(`${API}/admin/blogs/${blog.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const full = res.data;
      setEditingBlog(full);
      setFormData({
        title: full.title, category: full.category, featured_image: full.featured_image || '',
        content: full.content, meta_title: full.meta_title || '', meta_description: full.meta_description || '',
        status: full.status,
      });
      setShowForm(true);
    } catch { toast.error('Failed to load blog'); }
  };

  const handleDelete = async (blogId, title) => {
    if (!window.confirm(`Delete blog "${title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/blogs/${blogId}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Blog deleted');
      fetchBlogs();
    } catch { toast.error('Failed to delete'); }
  };

  const resetForm = () => setFormData({ title: '', category: '', featured_image: '', content: '', meta_title: '', meta_description: '', status: 'draft' });

  const openNew = () => { setEditingBlog(null); resetForm(); setShowForm(true); };

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button data-testid="back-to-dashboard" variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="text-white hover:bg-slate-800">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Blog Management</h1>
          </div>
          <Button data-testid="add-blog-button" onClick={openNew} className="bg-orange-500 hover:bg-orange-600 rounded-full" size="sm">
            <Plus className="w-4 h-4 mr-2" />New Blog
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? <div className="text-center py-12 text-slate-600">Loading...</div> : blogs.length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 mb-4">No blogs created yet</p>
              <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 rounded-full">
                <Plus className="w-4 h-4 mr-2" />Create First Blog
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {blogs.map(blog => (
              <Card key={blog.id} data-testid={`admin-blog-${blog.id}`} className="border-slate-100 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      {blog.featured_image && (
                        <img src={blog.featured_image} alt={blog.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0 hidden sm:block" />
                      )}
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-bold text-slate-900 truncate">{blog.title}</h3>
                          <Badge className={blog.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>{blog.status}</Badge>
                          <Badge variant="outline">{blog.category}</Badge>
                        </div>
                        <p className="text-sm text-slate-500 truncate">{blog.excerpt}</p>
                        <div className="flex items-center gap-3 flex-wrap">
                          <p className="text-xs text-slate-400">{formatDate(blog.created_at)} | slug: {blog.slug}</p>
                          <TranslationBadge blogId={blog.id} status={blog.status} />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {blog.status === 'published' && (
                        <Button data-testid={`preview-${blog.id}`} variant="outline" size="sm" onClick={() => window.open(`/blog/${blog.slug}`, '_blank')} className="rounded-full">
                          <Globe className="w-4 h-4" />
                        </Button>
                      )}
                      <Button data-testid={`edit-blog-${blog.id}`} variant="outline" size="sm" onClick={() => handleEdit(blog)} className="rounded-full">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button data-testid={`delete-blog-${blog.id}`} variant="destructive" size="sm" onClick={() => handleDelete(blog.id, blog.title)} className="rounded-full">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Blog Form Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBlog ? 'Edit Blog' : 'Create New Blog'}</DialogTitle>
            <DialogDescription>
              {editingBlog ? 'Update the blog details.' : 'Fill in the blog details.'} When published, translations to all languages will be generated automatically in the background.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input data-testid="blog-title-input" placeholder="Blog title" className="h-12 bg-slate-50" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Input data-testid="blog-category-input" placeholder="e.g., Logistics, Industry News" className="h-12 bg-slate-50" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Featured Image URL</Label>
              <Input data-testid="blog-image-input" placeholder="https://..." className="h-12 bg-slate-50" value={formData.featured_image} onChange={(e) => setFormData({...formData, featured_image: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Content *</Label>
              <div data-testid="blog-content-editor" className="bg-white rounded-md border border-slate-200">
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={(val) => setFormData({...formData, content: val})}
                  modules={quillModules}
                  placeholder="Write your blog content..."
                  style={{ minHeight: '250px' }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Meta Title (SEO)</Label>
                <Input data-testid="blog-meta-title" placeholder="SEO title (defaults to blog title)" className="h-12 bg-slate-50" value={formData.meta_title} onChange={(e) => setFormData({...formData, meta_title: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger data-testid="blog-status-select" className="h-12 bg-slate-50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Meta Description (SEO)</Label>
              <Textarea data-testid="blog-meta-desc" placeholder="Short SEO description (auto-generated from content if empty)" className="bg-slate-50" value={formData.meta_description} onChange={(e) => setFormData({...formData, meta_description: e.target.value})} />
            </div>
            <div className="flex space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1 rounded-full">Cancel</Button>
              <Button type="submit" data-testid="save-blog-button" className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-full" disabled={saving}>
                {saving ? 'Saving...' : editingBlog ? 'Update Blog' : 'Create Blog'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlogs;
